/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, serial, timestamp, jsonb } from "drizzle-orm/pg-core";
import { z } from "zod";

// Settings
export const settings = pgTable("settings", {
  id: serial("id").primaryKey(),
  key: text("key").notNull().unique(),
  value: jsonb("value").notNull(),
  description: text("description"),
  category: text("category").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Setting categories
export const SETTING_CATEGORIES = {
  STORAGE: 'storage',
  GENERAL: 'general',
  INTEGRATION: 'integration',
  SECURITY: 'security',
  LLM: 'llm',
  UNSPLASH: 'unsplash',
  HEYGEN: 'heygen',
  MEM0: 'mem0',
  HONCHO: 'honcho',
  MINTLIFY: 'mintlify',
  SALESFORCE: 'salesforce',
  SYSTEM: 'system',
  VERTEX_ANTHROPIC: 'vertex_anthropic',
  GCP_PROJECT: 'gcp_project',
  GCS_INTEGRATION: 'gcs_integration',
  SHAREPOINT_INTEGRATION: 'sharepoint_integration',
} as const;

export type SettingCategory = typeof SETTING_CATEGORIES[keyof typeof SETTING_CATEGORIES];

// Storage provider types
export const STORAGE_PROVIDERS = {
  GCS: 'google_cloud_storage',
  S3: 'amazon_s3',
  LOCAL: 'local',
} as const;

export type StorageProvider = typeof STORAGE_PROVIDERS[keyof typeof STORAGE_PROVIDERS];

// Setting keys
export const SETTING_KEYS = {
  // Image model settings
  DEFAULT_IMAGE_MODEL: 'default_image_model',
  
  // Storage settings
  STORAGE_CONFIG: 'storage_config',
  
  // LLM settings
  LLM_PROVIDERS: 'llm_providers',
  LLM_VENDORS: 'llm_vendors',
  
  // Unsplash API settings
  UNSPLASH_ACCESS_KEY: 'unsplashAccessKey',
  UNSPLASH_SECRET_KEY: 'unsplashSecretKey',
  UNSPLASH_APPLICATION_ID: 'unsplashApplicationId',
  
  // HeyGen API settings
  HEYGEN_API_KEY: 'heygen_api_key',
  HEYGEN_DEFAULT_AVATAR_ID: 'heygen_default_avatar_id',
  HEYGEN_DEFAULT_VOICE_ID: 'heygen_default_voice_id',
  
  // Web scraping API settings
  ZYTE_API_KEY: 'zyte_api_key',

  // Mem0 API settings
  MEM0_API_KEY: 'mem0_api_key',
  
  // Honcho API settings
  HONCHO_ENABLED: 'honcho_enabled',
  HONCHO_API_KEY: 'honcho_api_key',
  HONCHO_BASE_URL: 'honcho_base_url',
  
  // Mintlify API settings
  MINTLIFY_API_KEY: 'mintlify_api_key',
  MINTLIFY_DOMAIN: 'mintlify_domain',
  
  // Storage system settings
  SYSTEM_DRIVE_FOLDER_ID: 'system_drive_folder_id',
  
  // Salesforce API settings
  SALESFORCE_CLIENT_ID: 'salesforce_client_id',
  SALESFORCE_CLIENT_SECRET: 'salesforce_client_secret',
  
  // System settings (for remote auth mode)
  MASTER_SERVER_URL: 'master_server_url',
  LOGGED_IN_USER_ID: 'logged_in_user_id',
  LAST_USER_SYNC: 'last_user_sync',
  
  // Vertex AI Anthropic settings
  VERTEX_ANTHROPIC_ENABLED: 'vertex_anthropic_enabled',
  VERTEX_ANTHROPIC_PROJECT_ID: 'vertex_anthropic_project_id',
  VERTEX_ANTHROPIC_REGION: 'vertex_anthropic_region',
  
  // GCP Project settings (shared across integrations)
  GCP_PROJECT_ID: 'gcp_project_id',
  GCP_PROJECT_REGION: 'gcp_project_region',
  
  // GCS Integration settings
  GCS_ENABLED: 'gcs_enabled',
  GCS_PROJECT_ID: 'gcs_project_id',
  GCS_BUCKET_NAME: 'gcs_bucket_name',
  GCS_BUCKET_PREFIX: 'gcs_bucket_prefix',
  
  // SharePoint Integration settings
  SHAREPOINT_ENABLED: 'sharepoint_enabled',
  SHAREPOINT_TENANT_ID: 'sharepoint_tenant_id',
  SHAREPOINT_CLIENT_ID: 'sharepoint_client_id',
  SHAREPOINT_CLIENT_SECRET: 'sharepoint_client_secret',
  SHAREPOINT_SITE_URL: 'sharepoint_site_url',
} as const;

export type SettingKey = typeof SETTING_KEYS[keyof typeof SETTING_KEYS];

// Explicit schema for inserting settings (excludes auto-generated fields)
export const insertSettingSchema = z.object({
  key: z.string().min(1, "Setting key is required"),
  value: z.unknown(), // JSON value - can be any valid JSON
  description: z.string().optional().nullable(),
  category: z.string().min(1, "Setting category is required"),
});

// Inferred types from the database schema and insert schema
export type Setting = typeof settings.$inferSelect;
export type InsertSetting = z.infer<typeof insertSettingSchema>; 